// src/components/MobileBottomNav.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  SwipeableDrawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon      from '@mui/icons-material/Search';
import MenuIcon        from '@mui/icons-material/Menu';
import CloseIcon       from '@mui/icons-material/Close';
import AddIcon         from '@mui/icons-material/Add';
import { Home, ClipboardList, Undo2, Package, Building2, LineChart, Shield } from 'lucide-react';
import { navItem, istAktuelleSeite } from '../navigation/navItems'
import { BOTTOM_SHEET_TRANSITION, DUR, EASE, PRESS_ACTIVE_COARSE } from '../theme/tokens';

// ─── Apple glass helpers ──────────────────────────────────────────────────────

const glassBg = (t) =>
  t.palette.mode === 'dark'
    ? 'rgba(28,28,30,0.72)'
    : 'rgba(255,255,255,0.60)';

const glassBorder = (t) =>
  t.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.10)'
    : '1px solid rgba(255,255,255,0.65)';

const glassShadow = (t) =>
  t.palette.mode === 'dark'
    ? '0 8px 32px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.06)'
    : '0 8px 32px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.80)';

// Active "disc" highlight inside the pill
const activeDiscBg = (t) =>
  t.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.14)'
    : 'rgba(255,255,255,0.85)';

const activeDiscShadow = (t) =>
  t.palette.mode === 'dark'
    ? '0 1px 4px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)'
    : '0 1px 5px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.95)';

// ─── Sheet styling ────────────────────────────────────────────────────────────

const SHEET_PAPER_SX = {
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
  bgcolor: (t) =>
    t.palette.mode === 'dark' ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.97)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  pb: 'env(safe-area-inset-bottom)',
};

// ─── Create-menu data ─────────────────────────────────────────────────────────

// 🔴 Erzeugende Aktionen, KEINE Navigation — deshalb stehen sie NICHT im
// Katalog `navigation/navItems.jsx`. Sie haben ein Ziel, das es noch nicht
// gibt (eine neue Bestellung), und `New product` hat gar keinen Pfad, sondern
// einen `action`-Namen. Wer sie in den Katalog zoege, muesste dort Eintraege
// fuer Seiten anlegen, die niemand anwaehlt.
const CREATE_ITEMS = [
  { label: 'New purchase order', path: '/new',             icon: <ClipboardList size={18} strokeWidth={1.75} /> },
  { label: 'New case',           path: '/rma/new',         icon: <Undo2 size={18} strokeWidth={1.75} /> },
  { label: 'New product',        path: null,               icon: <Package size={18} strokeWidth={1.75} />, action: 'product' },
  { label: 'New vendor',         path: '/vendors/new',     icon: <Building2 size={18} strokeWidth={1.75} /> },
  { label: 'New competitor',     path: '/competitors/new', icon: <LineChart size={18} strokeWidth={1.75} /> },
];

// ─── More-menu data ───────────────────────────────────────────────────────────

const MORE_SECTIONS = [
  {
    title: 'Purchasing',
    items: [
      navItem('/po-inbox'),
      navItem('/ops/po-anomalies'),
      navItem('/ops/dep-health'),
      navItem('/ops/lsm-watcher'),
    ],
  },
  {
    title: 'After-Sales',
    items: [
      navItem('/rma'),
      navItem('/rma/inbox'),
      navItem('/rma/reports'),
      navItem('/rma/sla-overview'),
      navItem('/rma/analytics/inquiry-products'),
      navItem('/rma/inquiry-knowledge'),
      navItem('/reports/refunds'),
      // 🔴 W-17: Icons an die Sidebar angeglichen. Vorher trugen `Cases`,
      // `Inbox` und `Reports` ALLE `Undo2` — drei identische Symbole
      // nebeneinander in derselben Sektion, waehrend die Sidebar fuer dieselben
      // Routen `Inbox` und `BarChart3` fuehrt. Die Labels stimmten ueberein,
      // die Icons nicht; genau dafuer gibt es W-17.
    ],
  },
  {
    title: 'Catalogue',
    items: [
      navItem('/products'),
      navItem('/vendors'),
      navItem('/vendors/reports'),
      navItem('/taxonomy'),
      navItem('/media'),
    ],
  },
  {
    title: 'Intelligence',
    items: [
      navItem('/intelligence/market-lab'),
      navItem('/discovery'),
      navItem('/competitors'),
      navItem('/blog-sources'),
      navItem('/scrapers'),
    ],
  },
  {
    title: 'Operations',
    items: [
      navItem('/fulfillment'),
      navItem('/fca'),
      navItem('/incoming-inventory'),
      navItem('/kn-shipments'),
      navItem('/invoicing'), navItem('/monatsabschluss'),
      // Meldungsregister: Jahres- und Monatsmeldungen mit ihren Fristen. Bis
      // 2026-10-19 nur ueber die Befehlspalette erreichbar — `MORE_SECTIONS` ist ein
      // Literal, keine Ableitung aus `NAV_SECTIONS`.
      navItem('/declarations'),
      navItem('/admin/privacy'),
    ],
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function SheetHandle() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5 }}>
      <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: 'divider' }} />
    </Box>
  );
}

function SheetHeader({ title, onClose }) {
  return (
    <Stack
      direction="row" alignItems="center" justifyContent="space-between"
      sx={{ px: 2.5, pt: 0.5, pb: 1 }}
    >
      <Typography variant="h6" fontWeight={700}>{title}</Typography>
      <Box
        component="button"
        onClick={onClose}
        aria-label="Close"
        sx={{
          width: 44, height: 44, borderRadius: '50%',
          bgcolor: 'action.hover',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          border: 'none',
          p: 0,
          color: 'text.primary',
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </Box>
    </Stack>
  );
}

/** A single icon button inside the center pill */
function PillNavButton({ icon, active, onClick, label, current = active, expanded }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-label={label}
      // R16-A C1 / R16-B W4: `aria-pressed` ENTFERNT — es beschreibt einen
      // Umschalter („gedrueckt"), diese Pille ist ein Navigationsziel.
      // `aria-current="page"` ist die richtige Auszeichnung. `current` folgt
      // `active`; der „Mehr"-Knopf setzt es auf `false` (er OEFFNET ein Blatt),
      // sonst truegen bei offenem Blatt zwei Pillen `aria-current` (C-1).
      aria-current={current ? 'page' : undefined} aria-expanded={expanded}
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        transition: `background-color 0.18s cubic-bezier(0.4,0,0.2,1), box-shadow 0.18s cubic-bezier(0.4,0,0.2,1), color 0.18s cubic-bezier(0.4,0,0.2,1), transform ${DUR.press}ms ${EASE.standard}`,
        bgcolor: active ? activeDiscBg : 'transparent',
        boxShadow: active ? activeDiscShadow : 'none',
        color: active ? 'primary.main' : 'text.secondary',
        border: 'none',
        p: 0,
        // K-T1: der alte Ausschlag (0,88) kommt jetzt aus `PRESS_SCALE`, und
        // das Zeiger-Gatter kam dazu.
        // 🔴 Die Unterleiste rendert nur unter `down('sm')` (BREITE) — das ist
        // NICHT dasselbe wie ein grober Zeiger: ein schmales Desktop-Fenster
        // drueckt seitdem nicht mehr.
        ...PRESS_ACTIVE_COARSE,
      }}
    >
      {icon}
    </Box>
  );
}

/** Round floating glass button (left zone / search) */
function FloatingRoundButton({ icon, onClick, label, sx = {} }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-label={label}
      sx={{
        width: 52, height: 52,
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        bgcolor: glassBg,
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        border: glassBorder,
        boxShadow: glassShadow,
        color: 'text.secondary',
        transition: `transform ${DUR.press}ms ${EASE.standard}`,
        p: 0,
        // K-T1: der alte Ausschlag (0,90) kommt jetzt aus `PRESS_SCALE`,
        // Gatter wie am Disc darueber.
        ...PRESS_ACTIVE_COARSE,
        ...sx,
      }}
    >
      {icon}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MobileBottomNav({ onOpenSearch, onCreateStandalone }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen,   setMoreOpen]   = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const path = location.pathname;
  // 🔴 Die Kandidaten fuer die Aktiv-Marke des „Mehr"-Blatts: genau die Pfade,
  //   die das Blatt rendert. `MORE_SECTIONS` ist ein Literal, also ist das
  //   seine eigene Pfadliste — kein zweiter Ort, der driften koennte.
  const blattPfade = alleBlattPfade();
  const currentTab =
    path === '/'                                               ? 'home'   :
    path.startsWith('/overview') || path.startsWith('/purchase_order') || path === '/new' ? 'orders' :
    path.startsWith('/rma')                                                        ? 'cases'  : null;

  return (
    <>
      {/* ── Floating bottom strip ────────────────────────────────────────── */}
      <Box
        // Kennung fuer die Proben: die schwebende Leiste wird an genau EINEM
        // Element gesucht. Ohne sie muesste eine Probe „fest, unten, breit"
        // raten - und traefe auf der Fallansicht die Leiste einer fremden
        // Flaeche (gemessen im B5R.18-Rotlauf 2026-10-13).
        data-testid="mobile-bottom-nav"
        sx={{
          position: 'fixed',
          bottom: 0, left: 0, right: 0,
          zIndex: 1200,
          // Soft gradient so content fades into the nav naturally
          pt: 6,
          pb: 'calc(env(safe-area-inset-bottom) + 14px)',
          px: 2,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          pointerEvents: 'none',
          // 🔴 M1 (UI-Durchgang 2, Teil 5, 2026-10-09): der Verlauf ist am unteren
          // Anschlag DECKEND. Vorher trug er 0,22 (dunkel) bzw. 0,28 (hell) und
          // liess den Inhalt sichtbar unter der Pille stehen. Die Flaeche kommt
          // aus der Palette, nicht aus einem Hex-/`rgba`-Literal (Regel 22).
          // `alpha(…, 0)` liefert den durchsichtigen Gegenpol desselben Tokens.
          // 🔴 Nachbesserung 2026-10-09 (R16 W1 / R15 W1): EIN Token in BEIDEN
          // Modi — `background.default`. Vorher waehlte der Dunkelmodus
          // `background.paper` und legte damit einen HELLEREN Streifen
          // (`background.paper`) auf den Seitengrund (`background.default`): auf jeder Seite, deren
          // unterer Bereich blanke Schale ist, stand dort ein Streifen, den der
          // helle Modus nicht hat. Die Flaeche HINTER der Leiste ist in beiden
          // Modi `background.default` (`SidebarAndHeaderBar.jsx:2893`, das
          // `<main>` bis `100vh`; dazu der `V2.ink`-Wrapper `:2200`). `paper`
          // ist die Flaeche des Tabellenfusses, nicht die des Seitengrunds.
          // Entscheid des Plan-Eigentuemers (Orchestrator), 2026-10-09.
          background: (t) => {
            const deckend = t.palette.background.default;
            return `linear-gradient(to top, ${deckend} 0%, ${alpha(deckend, 0)} 100%)`;
          },
        }}
      >
        {/* Left — Search */}
        <FloatingRoundButton
          icon={<SearchIcon sx={{ fontSize: 22 }} />}
          onClick={onOpenSearch}
          label="Open search"
          sx={{ pointerEvents: 'auto' }}
        />

        {/* Center — 4-icon pill */}
        <Box
          role="navigation"
          aria-label="Primary"
          sx={{
            pointerEvents: 'auto',
            display: 'flex', alignItems: 'center',
            bgcolor: glassBg,
            backdropFilter: 'saturate(180%) blur(20px)',
            WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            border: glassBorder,
            borderRadius: 999,
            px: 0.5, py: 0.5,
            gap: 0.25,
            boxShadow: glassShadow,
          }}
        >
          <PillNavButton
            icon={<Home size={21} strokeWidth={currentTab === 'home'   ? 2.25 : 1.75} />}
            active={currentTab === 'home'}
            onClick={() => navigate('/')}
            label="Home"
          />
          <PillNavButton
            icon={<ClipboardList size={21} strokeWidth={currentTab === 'orders' ? 2.25 : 1.75} />}
            active={currentTab === 'orders'}
            onClick={() => navigate('/overview')}
            label="Purchase orders"
          />
          <PillNavButton
            icon={<Undo2 size={21} strokeWidth={currentTab === 'cases'  ? 2.25 : 1.75} />}
            active={currentTab === 'cases'}
            onClick={() => navigate('/rma')}
            label="After-sales cases"
          />
          <PillNavButton
            icon={<MenuIcon sx={{ fontSize: 21 }} />}
            active={moreOpen}
            // Kein `aria-current`: dieser Knopf OEFFNET ein Blatt, er ist keine
            // Seite. Sonst truege er bei offenem Blatt neben der aktiven Pille
            // ein zweites `aria-current="page"` (C-1).
            current={false} expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
            label="More options"
          />
        </Box>

        {/* Right — Add / Create */}
        <FloatingRoundButton
          icon={<AddIcon sx={{ fontSize: 22 }} />}
          onClick={() => setCreateOpen(true)}
          label="Create new"
          sx={{ pointerEvents: 'auto' }}
        />
      </Box>

      {/* ── Create bottom sheet ───────────────────────────────────────────── */}
      <SwipeableDrawer
        anchor="bottom"
        open={createOpen}
        onOpen={() => setCreateOpen(true)}
        onClose={() => setCreateOpen(false)}
        disableSwipeToOpen
        PaperProps={{ sx: SHEET_PAPER_SX }}

        {...BOTTOM_SHEET_TRANSITION}
      >
        <SheetHandle />
        <SheetHeader title="Create" onClose={() => setCreateOpen(false)} />
        <List sx={{ px: 1.5, pb: 2 }}>
          {CREATE_ITEMS.map((item) => (
            <ListItemButton
              key={item.label}
              onClick={() => {
                setCreateOpen(false);
                if (item.action === 'product') onCreateStandalone?.();
                else if (item.path) navigate(item.path);
              }}
              sx={{ borderRadius: 3, py: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: 500, fontSize: 15 }}
              />
            </ListItemButton>
          ))}
        </List>
      </SwipeableDrawer>

      {/* ── More bottom sheet ──────────────────────────────────────────────── */}
      <SwipeableDrawer
        anchor="bottom"
        open={moreOpen}
        onOpen={() => setMoreOpen(true)}
        onClose={() => setMoreOpen(false)}
        disableSwipeToOpen
        PaperProps={{ sx: SHEET_PAPER_SX }}

        {...BOTTOM_SHEET_TRANSITION}
      >
        <SheetHandle />
        <SheetHeader title="Menu" onClose={() => setMoreOpen(false)} />
        <Box sx={{ px: 1.5, pb: 2, maxHeight: '65vh', overflowY: 'auto' }}>
          {MORE_SECTIONS.map((section, si) => {
            // Der Waechter `if (!items.length) return null;` stand hier, solange
            // `section.items` durch `.filter(... || opsBadge.visible)` lief und die
            // Sektion „Daily" leer werden konnte. Mit dem Rueckbau des Daily Board
            // fiel der Filter weg; `MORE_SECTIONS` ist seither ein Literal, in dem
            // jede Sektion Eintraege hat. Der Waechter konnte nicht mehr feuern.
            const items = section.items;
            return (
            <Box key={section.title}>
              <Typography
                sx={{
                  px: 1.5, pt: si > 0 ? 1.5 : 0, pb: 0.5,
                  color: 'text.disabled',
                  fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
                }}
              >
                {section.title}
              </Typography>
              <List disablePadding>
                {items.map((item) => {
                  // 🔴 Der LAENGSTE passende Eintrag gewinnt (2026-10-07). Der
                  //   Praefix-Vergleich davor markierte auf `/rma/reports` sowohl
                  //   „Cases" (`/rma`) als auch „Reports" — zwei ausgewaehlte
                  //   Zeilen nebeneinander. Dieselbe Regel traegt hier auch
                  //   `aria-current`, deshalb kommen beide aus EINEM Ausdruck.
                  const active = istAktuelleSeite(item.path, path, blattPfade);
                  return (
                    <ListItemButton
                      key={item.path}
                      selected={active}
                      aria-current={active ? 'page' : undefined}
                      data-active={active ? 'true' : undefined}
                      onClick={() => { setMoreOpen(false); navigate(item.path); }}
                      sx={{ borderRadius: 3, py: 1.25 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'inherit' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontWeight: active ? 700 : 500,
                          fontSize: 15,
                          color: active ? 'primary.main' : 'text.primary',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
            );
          })}
          <Divider sx={{ my: 1 }} />
          <ListItemButton
            onClick={() => { setMoreOpen(false); navigate('/admin'); }}
            sx={{ borderRadius: 3, py: 1.25 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Shield size={18} strokeWidth={1.75} />
            </ListItemIcon>
            <ListItemText primary="Admin" primaryTypographyProps={{ fontWeight: 500, fontSize: 15 }} />
          </ListItemButton>
        </Box>
      </SwipeableDrawer>
    </>
  );
}

// R16-A C-1 / R16-B C1: die Kandidaten fuer `aria-current` im „Mehr"-Blatt.
// `MORE_SECTIONS` ist ein Literal ohne Voreinstellung — es ist genau das, was
// gerendert wird. Steht am Dateiende, damit keine Zeile davor wandert
// (Zeilenzeiger der Doku).
function alleBlattPfade() {
  return MORE_SECTIONS.flatMap((section) => section.items.map((item) => item.path))
}